import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { FilesFolders } from 'constants/files-folders-enum';
import {
  ALLOWED_TYPES_MODE_ID,
  ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ALLOWED_TYPES_MODE_STANDARD,
} from 'constants/upload-allowed-types';
import { verifyIdDocument } from 'helpers/document-upload-helpers';
import { getUploadAcceptConfig } from 'helpers/upload-allowed-types';
import { useBrandingStyleVariables } from 'hooks/branding-style-variables';
import { useVerifyIdDocumentByToken } from 'hooks/verify-id-document';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import DisplayBox from 'components/DisplayBox/DisplayBox';

import { DocumentUploadSlot } from '../DocumentUpload/DocumentUploadSlot';

// Stable defaults to avoid effect re-running every render (default params create new refs each time)
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

export const MultipleDocumentUpload = ({
  documentTitle = 'Select your files to upload',
  documentDescription = '',
  question,
  additionalInstructions = '',
  value = EMPTY_ARRAY, // array of upload objects
  modelParams = {},
  className,
  bucketFolder = FilesFolders.CandidatesApplicationDocuments,
  maxSize = 5242880,
  caption = '',
  accept,
  allowedTypesMode = ALLOWED_TYPES_MODE_STANDARD,
  idType = '',
  brand,
  useModel = false,
  disabled = false,
  metadata = EMPTY_OBJECT,
  listCode = '',
  verifyDocument = false,
  skipVerification = false,
  needsComplementaryDoc = false,
  complementaryInstructions = '',
  onChange = () => {},
  getUpload = async () => {},
  signUpload = async () => {},
  createUpload = async () => {},
  deleteUpload = async () => true,
  uploadId = null,
  handleStateOnVerify = () => {},
  fullWidth = false,
}) => {
  const { t } = useTranslation();
  const title = documentTitle || question?.text || question?.title || 'Select your files to upload';
  const description = documentDescription || question?.description || '';
  const { token } = useParams();
  const { verify: verifyByToken } = useVerifyIdDocumentByToken(token || undefined);

  const { effectiveAccept, allowedLabel, isFileAllowed } = getUploadAcceptConfig(
    allowedTypesMode,
    idType,
    accept,
    t,
  );

  const brandingStyleVariables = useBrandingStyleVariables(brand);
  const maxRetries = 3;

  const [uploads, setUploads] = useState([]);

  // Keep ref in sync so the value-sync effect can avoid overwriting during an in-flight upload
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;

  useEffect(() => {
    if (!value || !Array.isArray(value) || value.length === 0) {
      setUploads(prev => (prev.length === 0 ? prev : []));
      return;
    }
    const normalized = value.map(v => ({
      ...v,
      metadata: v?.metadata || { ...metadata, id_type: idType, attempts: 0 },
    }));
    setUploads(normalized);
  }, [value]);

  const callOnChange = useCallback(newUploads => onChange(newUploads), [onChange]);

  // Force-accept after max retries — preserved from the original component. Verify is
  // triggered fire-and-forget (see triggerVerify below) with no live-status callbacks wired
  // in, same as before, so `status` here realistically never reaches 'failed' through that
  // path today; kept only so a future real verify-status wire-up doesn't need to rebuild this.
  const validateRetryCountForUpload = useCallback(
    (upload, allUploads) => {
      const attempts = upload?.metadata?.attempts || 0;
      if (attempts >= maxRetries && upload?.status === 'failed') {
        const updated = allUploads.map(u =>
          u.id === upload.id ? { ...u, status: 'accepted' } : u,
        );
        setUploads(updated);
        callOnChange(updated);
        return updated;
      }
      return allUploads;
    },
    [callOnChange],
  );

  // Fire-and-forget: creates the backend id_upload_detail record for this upload. No live
  // pass/fail status is wired back into the UI — matches the original component's behavior.
  const triggerVerify = async (uploadedId, uploadedMetadata) => {
    if (!(verifyDocument && listCode && token && uploadedId)) return;
    try {
      await verifyIdDocument(
        {
          uploadId: uploadedId,
          metadata: uploadedMetadata,
          listCode,
          expiryTolerance: null,
          verifyByToken,
          reTakeSelfie: false,
          getUpload,
          useModel: true,
          value: { id: uploadedId, metadata: uploadedMetadata },
          skipVerification,
        },
        {
          setVerifying: () => {},
          setStatus: () => {},
          setError: () => {},
          setErrorType: () => {},
          setVerificationError: () => {},
          setShowSelfieModal: () => {},
          onChange: () => {},
          setIsFileRejected: () => {},
        },
      );
    } catch (verifyErr) {
      console.error('Error creating id_upload_detail:', verifyErr);
    }
  };

  // Shared by both the "add a new file" slot (prevItem === null) and any list item's own
  // Replace action (prevItem === that item) — the slot itself already handled the raw
  // upload/replace mechanics (including deleting the old upload on a replace) before calling
  // this; here we only need to fold the result into our own `uploads` list.
  const handleSlotChange = async (prevItem, newUpload) => {
    if (newUpload === null) {
      // Delete — the slot already called deleteUpload itself.
      const updated = uploads.filter(u => u.id !== prevItem.id);
      setUploads(updated);
      callOnChange(updated);
      return;
    }

    const uploadedMetadata = newUpload?.metadata || metadata;

    const updated = prevItem
      ? uploads.map(u => (u.id === prevItem.id ? newUpload : u))
      : [...uploads, newUpload];
    setUploads(updated);

    await triggerVerify(newUpload.id, uploadedMetadata);
    const finalList = validateRetryCountForUpload(newUpload, updated);
    callOnChange(finalList);
  };

  return (
    <div
      className={cn(className, { 'u-disabled': disabled, '!w-full !max-w-full': fullWidth })}
      style={brandingStyleVariables}>
      <div className="u-padding surface-white u-border u-border-radius--medium u-drop-shadow--xs d-flex flex-column u-gap-medium">
        <div className="d-flex flex-column u-gap-x-small">
          {caption && (
            <span className="tertiary-text u-font-size-small u-font-weight-500">{caption}</span>
          )}
          <span className="u-font-size-large u-font-weight-500">{title}</span>
          {description && (
            <span className="secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line">
              {description}
            </span>
          )}
          <span className="text-lg text-neutral-6">Max size: {maxSize / 1024 / 1024}MB</span>
          {additionalInstructions && (
            <span
              className={cn(
                'secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line',
                description && 'u-margin-top--small',
              )}>
              {additionalInstructions}
            </span>
          )}
        </div>

        {uploads.map(item => {
          const nextAttempts = (item.metadata?.attempts || 0) + 1;
          return (
            <DocumentUploadSlot
              key={item.id}
              heading={title}
              showTitle={false}
              brand={brand}
              disabled={disabled}
              value={item}
              useModel={true}
              modelParams={modelParams}
              metadata={{
                ...metadata,
                id_type: idType,
                attempts: nextAttempts,
                ...(listCode ? { id_requirements_list: listCode } : {}),
              }}
              bucketFolder={bucketFolder}
              maxSize={maxSize}
              accept={effectiveAccept}
              acceptLabel={allowedLabel}
              isFileAllowed={isFileAllowed}
              getUpload={getUpload}
              signUpload={signUpload}
              createUpload={createUpload}
              deleteUpload={deleteUpload}
              onUploadingChange={isUploading => handleStateOnVerify(isUploading)}
              onChange={newValue => handleSlotChange(item, newValue)}
            />
          );
        })}

        <DocumentUploadSlot
          heading={title}
          showTitle={false}
          brand={brand}
          disabled={disabled}
          value={null}
          useModel={true}
          resetAfterUpload
          modelParams={modelParams}
          metadata={{
            ...metadata,
            id_type: idType,
            attempts: 1,
            ...(listCode ? { id_requirements_list: listCode } : {}),
          }}
          bucketFolder={bucketFolder}
          maxSize={maxSize}
          accept={effectiveAccept}
          acceptLabel={allowedLabel}
          isFileAllowed={isFileAllowed}
          getUpload={getUpload}
          signUpload={signUpload}
          createUpload={createUpload}
          deleteUpload={deleteUpload}
          onUploadingChange={isUploading => handleStateOnVerify(isUploading)}
          onChange={newValue => handleSlotChange(null, newValue)}
        />

        {needsComplementaryDoc && complementaryInstructions && (
          <DisplayBox show={true} alignLeft>
            <span className="u-font-size-small u-font-weight-400">{complementaryInstructions}</span>
          </DisplayBox>
        )}
      </div>
    </div>
  );
};

MultipleDocumentUpload.propTypes = {
  documentTitle: PropTypes.string,
  documentDescription: PropTypes.string,
  question: PropTypes.object,
  additionalInstructions: PropTypes.string,
  value: PropTypes.array,
  modelParams: PropTypes.object,
  className: PropTypes.string,
  bucketFolder: PropTypes.string,
  maxSize: PropTypes.number,
  caption: PropTypes.string,
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  allowedTypesMode: PropTypes.oneOf([
    ALLOWED_TYPES_MODE_STANDARD,
    ALLOWED_TYPES_MODE_ID,
    ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ]),
  idType: PropTypes.string,
  brand: PropTypes.object,
  useModel: PropTypes.bool,
  disabled: PropTypes.bool,
  metadata: PropTypes.object,
  listCode: PropTypes.string,
  verifyDocument: PropTypes.bool,
  skipVerification: PropTypes.bool,
  needsComplementaryDoc: PropTypes.bool,
  complementaryInstructions: PropTypes.string,
  onChange: PropTypes.func,
  getUpload: PropTypes.func,
  signUpload: PropTypes.func,
  createUpload: PropTypes.func,
  deleteUpload: PropTypes.func,
  uploadId: PropTypes.number,
  handleStateOnVerify: PropTypes.func,
  fullWidth: PropTypes.bool,
};
